import { logError } from 'src/utils/logger';

import {
  FONT_FACE_REGEX,
  DEFAULT_OPTIONS,
  FONT_FACE_PROPERTY_REGEX,
  CAMEL_CASE_REGEX,
  FONT_FACE_SOURCE_REGEX,
  FONT_FACE_PROPERTY_PREFIX_REGEX,
  FONT_FAMILY_REGEX,
  FONT_FALLBACK,
  FONT_FACE_TYPES
} from './font.constants';
import { getFontStretch } from 'assets/js/editor/text/text-helpers';

export class FontService {
  log = logError(this.constructor.name);

  // fonts = [{ family: 'Lato', customFontId: '102015573', variants: [{ weight: '100', style: 'normal' }] }]

  async getFontsCSS (fonts, givenOptions) {
    const options = { ...DEFAULT_OPTIONS, ...givenOptions };

    const cssTexts = await Promise.all(fonts.map(async ({ family, customFontId, variants }) => {
      const font = await this.getFont(family, { ...options, customFontId, variants });
      const cssText = await this.getFontCSS(font);

      return cssText;
    }));

    return cssTexts.flat().join('');
  }

  async loadFonts (fonts, options = DEFAULT_OPTIONS) {
    const fontFaces = await Promise.all(fonts.map(({ family, customFontId, variants }) => (
      this.getFontFaces(family, { ...options, customFontId, variants })
    )));

    const fontFacesFlat = fontFaces.flat();

    return await Promise.all(fontFacesFlat.map(async ({ family, sources, src, ...descriptors }) => {
      const fontFace = new FontFace(family, src, descriptors);
      await fontFace.load();
      document.fonts.add(fontFace);

      return fontFace;
    }));
  }

  async getFontCSS ({ family, sources, variants, subsets, earlyAccess }) {
    if (sources.length) {
      const cssText = this.parseFontSourcesAsCSSText(family, sources.map((source, index) => ({ ...source, stretch: variants[index].stretch })));

      return cssText;
    }

    if (!variants.length) return '';

    // TODO: combine families into one request
    const url = this.getGoogleFontsUrl(family, variants, subsets, earlyAccess);

    if (!url) return '';

    const cssText = await this.fetchCssText(url);
    return cssText;
  }

  async getFontFaces (family, options = DEFAULT_OPTIONS) {
    const font = await this.getFont(family, options);

    if (options.skipDeleted && font.isDeleted) return [];

    const cssText = await this.getFontCSS(font);

    if (!cssText) return [];

    const fontFaces = this.parseFontFaces(cssText);

    return fontFaces;
  }

  async getFont (family, options = DEFAULT_OPTIONS) {
    const { customFontId } = { ...options, ...DEFAULT_OPTIONS };

    const fallBackFont = FONT_FALLBACK;

    await INKIVE.FontsFactory.getCustomFont(customFontId);
    const factoryFont = INKIVE.FontsFactory.get(family) || INKIVE.FontsFactory.get(fallBackFont);

    if (!factoryFont) {
      const error = new Error(`Font ${family} was not found`);
      this.log(error);

      throw error;
    }

    const font = this.parseFactoryFont(factoryFont);

    const variants = font.variants.filter((variant) => {
      if (options.skipLoaded && variant.isLoaded) return false;

      if (options.variants?.length) {
        const isFiltered = options.variants.some(({ weight, style, id }) => (
          (id && id === variant.id) ||
          (weight === variant.weight && style === variant.style)
        ));

        const isDefault = variant.variant === '400';

        return isFiltered || isDefault;
      }

      return true;
    });

    const sources = variants
      .map((variant) => ({
        ...variant,
        source: font.sources.find(({ variant: sourceVariant, id }) =>
          id && variant.id ? variant.id === id : sourceVariant === variant.variant
        )
      }))
      .filter(({ source }) => source)
      .map(({ source: { source, type }, style, weight }) => {
        return {
          url: INKIVE.Helpers.getCORSSecureUrl(source),
          format: FONT_FACE_TYPES[type] || type,
          style,
          weight
        };
      });

    return { ...font, sources, variants };
  }

  async fetchCssText (url) {
    const httpsUrl = url.replace('http://', 'https://');
    try {
      const cssText = await this.fetchURL(httpsUrl);
      return cssText;
    } catch (error) {
      const fallbackURL = (new URL('/assets/css/fonts/nunito.css', document.baseURI)).href;
      const cssText = await this.fetchURL(fallbackURL);

      return cssText;
    }
  }

  async fetchURL (url, key) {
    const response = await fetch(url);
    const cssText = await response.text();

    return cssText;
  }

  parseFactoryFont ({
    customId,
    family: _family,
    name: _name,
    isDeleted,
    isSystem,
    templateFonts,
    imported_css: importedCss,
    earlyaccess: earlyAccess,
    support: {
      b: supportsBold = false,
      bi: supportsBoldItalic = false,
      i: supportsItalic = false,
      sources = [
        // {
        //   'source': 'https://s3.amazonaws.com/virginia.webrand.com/virginia/company/library/0000000001/font/qqqewerqiuqradi/dankmono-regular.ttf',
        //   'type': 'ttf',
        //   'variant': '400'
        // }
      ],
      variants: _variants = [
        // '400',
      ],
      subsets: _subsets = [
        // 'latin-ext',
        // 'latin'
      ]
    }
  }) {
    const family = (_family || _name).toString().replaceAll('\'', '').replaceAll('"', '');
    const subsets = [...new Set(['latin-ext', 'cyrillic', ..._subsets])];

    const variants = this.getFontVariants(family, _variants, sources);

    return {
      customId,
      family,
      variants,
      sources,
      subsets,
      earlyAccess,
      isDeleted
    };
  }

  fontFaceSetIteratorToArray (target) {
    const iterable = target.values();
    const results = [];
    let iterator = iterable.next();

    while (iterator.done === false) {
      results.push(iterator.value);

      iterator = iterable.next();
    }

    return results;
  }

  getFontVariants (family, variants, sources) {
    const documentFonts = this.fontFaceSetIteratorToArray(document.fonts);

    return variants.map(({ variant, id }) => {
      const { weight, style } = this.parseFontVariant(variant);
      const source = sources.find(({ id: sourceId }) => id === sourceId);
      const stretch = getFontStretch(source?.width);

      // document.fonts.check() returns false for fonts already loaded by CSS, e.g. 'normal 400 12px Lato'
      const isLoaded = documentFonts.some((font) => (
        font.family === family &&
        font.weight === weight &&
        font.style === style &&
        font.stretch === stretch &&
        font.loaded
      ));

      return { weight, style, variant, id, isLoaded, stretch };
    });
  }

  parseFontVariant (variant) {
    const [, weight, style] = variant.split(/([0-9]+)/);

    return {
      weight: weight || '400',
      style: style || 'normal'
    };
  }

  parseFontSourcesAsCSSText (family, variants) {
    return variants.map(({ url, format, style, weight, stretch }) => {
      return [
        '@font-face {',
        `font-family: '${family}';`,
        `font-style: ${style};`,
        `font-weight: ${weight};`,
        `font-stretch: ${stretch};`,
        `src: url(${url}) format('${format}');`,
        '}'
      ].join('\n');
    }).join('\n');
  }

  getGoogleFontsUrl (family, variants, subsets, earlyAccess) {
    // https://fonts.google.com/earlyaccess
    if (earlyAccess) return `https://fonts.googleapis.com/earlyaccess/${earlyAccess}.css`;

    const variations = variants.map(({ weight, style }) => (
      `${weight}${style === 'italic' ? 'i' : ''}`
    )).join(',');

    const fontFamily = `${family}:${variations}`;

    const fontSubsets = subsets.join(',');

    const url = new URL('https://fonts.googleapis.com/css');
    url.searchParams.set('family', fontFamily);
    if (fontSubsets) url.searchParams.set('subset', fontSubsets);

    return url.toString();
  }

  parseFontFamilies (html) {
    const fontFamilies = [];

    let match;
    while ((match = FONT_FAMILY_REGEX.exec(html)) !== null) {
      const [, family] = match;
      fontFamilies.push(family);
    }

    return fontFamilies;
  }

  parseFontFaces (cssText) {
    const fontFaces = [];

    let match;
    while ((match = FONT_FACE_REGEX.exec(cssText)) !== null) {
      const fontFaceCSSText = match[1];
      const fontFace = this.parseFontFace(fontFaceCSSText);
      fontFaces.push(fontFace);
    }

    return fontFaces;
  }

  parseFontFace (fontFaceCSSText) {
    const fontFace = {};

    let match;
    while ((match = FONT_FACE_PROPERTY_REGEX.exec(fontFaceCSSText)) !== null) {
      const [, property, value] = match;

      const parsedProperty = property
        .replace(FONT_FACE_PROPERTY_PREFIX_REGEX, '')
        .replace(CAMEL_CASE_REGEX, (_, letter) => letter.toUpperCase());

      const parsedValue = value
        .trim()
        .replaceAll(/['"]/g, '');

      fontFace[parsedProperty] = parsedValue;
    }

    const sources = this.parseFontFaceSources(fontFaceCSSText);

    // TODO: Support sources in single fontface
    const src = sources[0].url;

    return {
      ...fontFace,
      sources,
      src
    };
  }

  parseFontFaceSources (cssTextSrc) {
    const fontFaceSources = [];

    let match;
    while ((match = FONT_FACE_SOURCE_REGEX.exec(cssTextSrc)) !== null) {
      const [url, format] = match;
      const type = this.#getFontType(format);
      fontFaceSources.push({ url, format, type });
    }

    return fontFaceSources;
  }

  #getFontType (format) {
    return Object.keys(FONT_FACE_TYPES).find(key => FONT_FACE_TYPES[key] === format) || format;
  }
}
